import React from 'react';
import { Theme } from '../../utils/themes';
import { useNavigate } from 'react-router-dom';
import { Lock, Star, Check } from 'lucide-react';

interface ThemePathwayProps {
  themes: Theme[];
  themesProgress: any[];
}

export default function ThemePathway({ themes, themesProgress }: ThemePathwayProps) {
  const navigate = useNavigate();

  const getThemeStatus = (theme: Theme) => {
    const progress = themesProgress.find(t => t.theme === theme.name)?.progress || 0;
    const totalWords = theme.words.length;

    // 5 points per word is the target for 100% completion
    const targetPoints = totalWords * 5;
    const percentage = Math.min(100, Math.floor((progress / targetPoints) * 100)) || 0;
    const isCompleted = percentage >= 100;

    if (isCompleted) return { status: 'completed', percentage };
    return { status: 'active', percentage };
  };

  return (
    <div className="flex flex-col items-center py-12 max-w-md mx-auto relative">
       {/* Svg line connecting nodes would go here, but doing it with CSS pseudo-elements or absolute divs is easier for dynamic list */}

       {themes.map((theme, index) => {
         const { status, percentage } = getThemeStatus(theme);

         // Zig-zag offset
         // 0: center, 1: right, 2: center, 3: left, 4: center...
         // Or Duolingo style: center -> slightly right -> center -> slightly left
         const offset = index % 4 === 1 ? 'translate-x-12' : index % 4 === 3 ? '-translate-x-12' : '';

         return (
           <div key={theme.id} className={`relative mb-8 ${offset} z-10`}>
             <button
                onClick={() => {
                  navigate(`/theme/${theme.id}`);
                }}
                className={`
                  w-24 h-24 rounded-full flex flex-col items-center justify-center border-b-8 transition-all duration-200 transform
                  ${status === 'completed'
                    ? 'bg-yellow-400 border-yellow-600 text-yellow-900 active:border-b-0 active:translate-y-2'
                    : status === 'active'
                      ? 'bg-purple-500 border-purple-700 text-white hover:bg-purple-400 active:border-b-0 active:translate-y-2'
                      : 'bg-gray-200 border-gray-300 text-gray-400 cursor-not-allowed'
                  }
                `}
             >
                <span className="text-3xl mb-1">{theme.icon}</span>
                {status === 'completed' && (
                  <div className="absolute -top-2 -right-2 bg-yellow-300 rounded-full p-1 border-2 border-yellow-500">
                    <Check className="w-4 h-4 text-yellow-800" />
                  </div>
                )}
             </button>

             {/* Label below button */}
             <div className="mt-2 text-center">
               <span className={`font-bold text-sm bg-white px-2 py-1 rounded-lg shadow-sm text-gray-800`}>
                 {theme.name.split('(')[0].trim()}
               </span>
             </div>

             {/* Stars / Progress */}
             <div className="flex justify-center mt-1 space-x-1">
               {[1, 2, 3].map(star => (
                 <Star
                   key={star}
                   className={`w-3 h-3 ${percentage >= (star * 33) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                 />
               ))}
             </div>

            {/* Connecting Path Line (Draw downwards to next item if exists) */}
            {index < themes.length - 1 && (
                <div
                    className="absolute top-20 left-1/2 w-20 h-24 -z-10 pointer-events-none"
                    style={{
                         // This is tricky without SVG. Let's try to just render absolute svg lines in the parent container
                         // or just simple vertical lines if no zig-zag.
                         // With zig-zag, we need SVGs.
                         display: 'none'
                    }}
                >
                </div>
            )}
           </div>
         );
       })}

       {/* Background SVG Path */}
       <svg className="absolute top-0 left-0 w-full h-full pointer-events-none -z-0" style={{ overflow: 'visible' }}>
          {themes.map((_, index) => {
            if (index === themes.length - 1) return null;
            // Calculate coordinates for current and next
            // Center X = 50%
            // Offset X = 50% + (index % 4 === 1 ? 48 : index % 4 === 3 ? -48 : 0)
            // Y spacing approx 144px (96px button + 32px margin + label)

            const getX = (i) => {
                 const base = 50; // percent
                 if (i % 4 === 1) return 'calc(50% + 48px)';
                 if (i % 4 === 3) return 'calc(50% - 48px)';
                 return '50%';
            };

            const yStart = 48 + (index * 160); // approximate center of button
            const yEnd = 48 + ((index + 1) * 160);

            // We can't easily mix calc and px in path d attribute in a generic way without fixed width
            // But since max-w-md is fixed width (approx), let's assume pixel values.
            // max-w-md is 448px. Center is 224px.
            // offset 12 tailwind units = 48px.

            const centerX = 224;
            const offsetX = 48;

            const getPxX = (i) => {
                if (i % 4 === 1) return centerX + offsetX;
                if (i % 4 === 3) return centerX - offsetX;
                return centerX;
            }

            const x1 = getPxX(index);
            const x2 = getPxX(index + 1);
            const y1 = 48 + (index * 148); // Fine-tune this based on actual height
            const y2 = 48 + ((index + 1) * 148);

            return (
                <path
                    key={`path-${index}`}
                    d={`M ${x1} ${y1} C ${x1} ${y1 + 50}, ${x2} ${y2 - 50}, ${x2} ${y2}`}
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray="10 10"
                />
            );
          })}
       </svg>
    </div>
  );
}
